import React, { useState, useMemo, useRef, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next'
import {
    Progress,
    Button,
    Spinner,
    Input,
    FormFeedback,
    FormGroup,
    ListGroup,
    ListGroupItem,
    Media,
    CardText,
    Form,
    CustomInput,
    Label,
    Badge
} from 'reactstrap'
import bootbox from 'components/Modals/Bootbox'
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'components/Shared/Toast';
import clsx from 'clsx';
import DatePicker from 'components/Shared/DatePicker'
import produce from 'immer';
import { useFieldArray, useForm, useWatch } from 'react-hook-form'

import { parseDate, formatCurrency, emailRegex, getMongoObjectId, format } from 'utils'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { ProfileCan } from 'context/profile'
import useKeyPress from 'hooks/useKeyPress'
import { fetchProperty, contractAction } from 'api/actions/contracts'
import { uploadFileV2 as uploadFileAction } from 'api/actions/file';
import { 
    fetchMedicalPrescriptionList, 
    createMedicalPrescription,
    updateMedicalPrescription,
    deleteMedicalPrescription
} from 'api/actions/medical-prescriptions';
import { fetcher } from 'api/client'
import useFileUpload from 'api/hooks/file/useFileUpload';
import { Link } from 'react-router-dom';


async function uploadFile({ file }) {
    return uploadFileAction(file);
}

function MedicalPrescriptions({ contract = {}, refetchList, isTemplate }) {
    const { t } = useTranslation(['translation', 'validations', 'glossary'])
    const queryClient = useQueryClient();
    const [listMode, setListMode] = useState('calendar')
    const isEditable = contract.status === '';
    // const [progressTotal, setProgressTotal] = useState(0);
    const { data, status } = useQuery(['contract', contract._id, 'medical-prescriptions'], ({ queryKey: [, contractId] }) => fetcher(fetchMedicalPrescriptionList({ contractId })), {
        enabled: !!contract._id,
        onSuccess: (data) => {
        },
        onError: (error) => {
            // CHECK CancelledError {silen: undefined}
            // toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const progressTotal = useMemo(() => {
        if (!data?.length) return 0;
        const medicines = data.flatMap(medicalPrescription => medicalPrescription.medicines)
            
        const total = medicines.reduce((count, medicine) => {
            count += medicine.isUnlimited ? 1 : (Number(medicine.quantity) || 0);
            return count
        }, 0);

        const count = medicines.reduce((count, medicine) => {
            count += medicine.isUnlimited ? 1 : (Number(medicine.quantitySupplied) || 0);
            return count
        }, 0);
        const percentage = Math.min(Math.floor(total ? (count / total) * 100 : 0), 100);

        return percentage || 0;

    }, [data])

    const { mutate: mutateAddMedicalPrescription } = useMutation((payload) => fetcher(createMedicalPrescription(payload)), {
        onSuccess: (data, variables) => {
            toast.success(t('validations:messages.successfullyCreated', { context: 'female', entity: t('glossary:medical-prescription') }))
            queryClient.invalidateQueries(['contract', contract._id, 'medical-prescriptions'])
            queryClient.invalidateQueries(['contract', contract._id, 'timeline'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const handleAddMedicalPrescription = () => {
        mutateAddMedicalPrescription({
            contractId: contract._id,
            name: t('Nueva receta médica'),
        })
    }

    return (
        <div className="h-100">
            <div className="detail p-2">
                <div className="d-flex justify-content-between mb-2">
                    <span>{t('Surtido')}: {progressTotal}%</span>
                    {/* <span className="pointer calendar-view" onClick={toggleListMode}>
                        <i className={`fas fa-${listMode}`}></i>
                    </span> */}
                </div>
                <Progress
                    className="progress-md mb-2"
                    color="primary"
                    striped
                    value={progressTotal}
                />
                {/* <ProfileCan I="modify" this={contract}>
                    {
                        () =>  */}
                <div className="text-center border" hidden={!isEditable}>
                    <Button
                        block
                        size="sm"
                        color="default"
                        data-action="add-milestone"
                        onClick={handleAddMedicalPrescription}
                    >
                        + {t('contracts.milestones.add')}
                    </Button>
                </div>
                {/* }
                </ProfileCan> */}
            </div>
            <div className="milestones-listing overflow-x-hidden overflow-y-auto" style={{height: 'calc(100% - 220px)'}}>
                <MedicalPrescriptionList contract={contract} list={data} refetchList={refetchList} isEditable={isEditable} />
            </div>
        </div>

    )
};

export default MedicalPrescriptions;

function MedicalPrescriptionList({ contract, list = [], refetchList, isEditable }) {
    const { t } = useTranslation()

    return (
        <ListGroup>
            {
                list?.map(medicalPrescription => (
                    <MedicalPrescriptionContainer
                        key={medicalPrescription._id}
                        medicalPrescription={medicalPrescription}
                        contractId={contract._id}
                        isEditable={isEditable}
                    />
                ))
            }
            {
                !list?.length &&
                <ListGroupItem className="text-center">
                    {t('No se ha configurado ninguna receta médica para este documento.')}
                </ListGroupItem>
            }
        </ListGroup>
    )
}

function MedicalPrescriptionContainer(props) {
    const [isEditing, setIsEditing] = useState(false);


    if (isEditing) return (
        <MedicalPrescriptionFormItem {...props} onEditing={setIsEditing} />  
    )

    return (
        <MedicalPrescriptionItem {...props} onEditing={setIsEditing} />  
    )
}

function MedicalPrescriptionItem({ medicalPrescription, onEditing, isEditable }) {
    const { t } = useTranslation()
    return (
        <div className="p-2 border rounded">
            <div className="d-flex justify-content-end p-1">
                <span
                    className="ml-2 pointer"
                    onClick={() => onEditing(true)}
                    hidden={!isEditable}
                >
                    <i className="fas fa-edit" />
                </span>
                <Link
                    className="ml-2 pointer text-dark"
                    to={`/contracts/${medicalPrescription.contract?._id}/medical-prescriptions/${medicalPrescription._id}`} 
                    target="_blank" 
                >
                    <i className="fas fa-external-link-alt" />
                </Link>
            </div>
            <MedicalPrescriptionProperty label={t('milestones.name')} value={medicalPrescription.name} />
            {/* <MedicalPrescriptionProperty label={t('Documento')} value={medicalPrescription.contract?.name} /> */}
                <div className="font-weight-bold">Medicamentos:</div> 
                <ul>
                    {
                        medicalPrescription?.medicines?.map((medicine) => (
                            <li className="pl-2">
                                <MedicalPrescriptionProperty label={t('milestones.name')} value={medicine.name || 0} />
                                <MedicalPrescriptionProperty label={t('Cantidad')} value={medicine.isUnlimited ? 'Ilimitada' : (medicine.quantity || 0)} />
                                <MedicalPrescriptionProperty label={t('Cantidad surtida')} value={medicine.quantitySupplied || 0} />
                                {!!medicine.supplyHistory?.length &&
                                    <div className="">
                                        <div className="font-weight-bold">
                                            {t('Historial')}:
                                        </div>
                                        {
                                            medicine.supplyHistory.map(history => (
                                                <div className="p-2 border-bottom" key={history._id}>
                                                    <MedicalPrescriptionProperty label={t('Nombre de la farmacia que surte')} value={history.pharmacy} />
                                                    { history.responsible && <MedicalPrescriptionProperty label={t('Nombre de la persona que surte')} value={history.responsible} /> }
                                                    <MedicalPrescriptionProperty label={t('Cantidad surtida')} value={history.quantity} />
                                                    <div className="text-muted">
                                                        {format(history.createdAt, 'PPp')}
                                                    </div>
                                                    <div className="font-weight-bold" hidden={!history?.attachments?.length}>
                                                        {t('Evidencia')}:
                                                    </div>
                                                    <ul className="">
                                                        {history?.attachments?.map((attachment, i) => {
                                                            return (
                                                                <li key={`${attachment._id}-${i}`}>
                                                                    <a className="text-break text-dark" href={attachment.url} target="_blank" rel="noopener noreferrer">{attachment.name}</a>
                                                                </li>
                                                            )
                                                        })}
                                                    </ul>
                                                </div>
                                            ))
                                        }

                                    </div>
                                }
                            </li>

                        ))
                    }
                    {
                        !medicalPrescription?.medicines?.length && 
                         t('No se ha configurado ningún medicamento para esta receta médica.')
                    }
                </ul>
           
            
        </div>
    )
}

function MedicalPrescriptionProperty({ label, value, isArray }) {
    return (
        <div>
            <span className="font-weight-bold">{label}:</span> {value}
        </div>
    )
}

function MedicalPrescriptionFormItem({ medicalPrescription, contractId, onDeleteAttachment, onEditing }) {
    const { t, i18n } = useTranslation(['translation', 'validations', 'glossary'])
    const queryClient = useQueryClient();
    const { register, control, setValue, handleSubmit } = useForm({
        defaultValues:{
            name: medicalPrescription.name,
            medicines: medicalPrescription.medicines,
        }
    })
    

    const { _id: medicalPrescriptionId, completed, name, attachments} = medicalPrescription;

    const { fields, append } = useFieldArray({
        control, 
        name: 'medicines',
    })
    
    const { mutate: mutateDeleteMedicalPrescription } = useMutation(({ medicalPrescriptionId }) => fetcher(deleteMedicalPrescription(medicalPrescriptionId)), {
        onMutate: (variables) => {
            queryClient.cancelQueries(['contract', contractId, 'medical-prescriptions'])

            const previousValue = queryClient.getQueryData(['contract', contractId, 'medical-prescriptions'])

            queryClient.setQueryData(['contract', contractId, 'medical-prescriptions'], oldData => {
                console.log(oldData)
                return oldData.filter(m => m._id !== medicalPrescription._id);
            })

            return previousValue
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'medical-prescriptions'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
        },
        onError: (error, variables, previousValue) => {
            queryClient.setQueryData(['contract', variables.contractId, 'medical-prescriptions'], previousValue)
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const { mutate: mutateUpdateMedicalPrescription } = useMutation(({ medicalPrescriptionId, ...payload }) => fetcher(updateMedicalPrescription(medicalPrescriptionId, payload)), {
        onMutate: (variables) => {
            queryClient.cancelQueries(['contract', contractId, 'medical-prescriptions'])

            const previousValue = queryClient.getQueryData(['contract', contractId, 'medical-prescriptions'])

            queryClient.setQueryData(['contract', contractId, 'medical-prescriptions'], produce(draft => {

                const index = draft.findIndex(m => m._id === medicalPrescription._id);
                if (index !== -1) draft[index].name = variables.name
            }))

            return previousValue
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'medical-prescriptions'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
            onEditing(false)
        },
        onError: (error, variables, previousValue) => {
            queryClient.setQueryData(['contract', variables.contractId, 'medical-prescriptions'], previousValue)
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const handleSave = useCallback(async (data) => {
        const medicines = data.medicines.map((medicine, i) => {
            const medicineFound = medicalPrescription.medicines.find(m => m._id === medicine._id)
            if (medicineFound) {
                return {
                    ...medicineFound,
                    ...medicine,
                }
            }

            return medicine
        })

        console.log({data})

        console.log({medicines})
        const payload = {
            ...medicalPrescription,
            ...data,
            medicines,
            contractId,
            medicalPrescriptionId: medicalPrescription._id,
        }
        mutateUpdateMedicalPrescription(payload)
    }, [medicalPrescription, mutateUpdateMedicalPrescription, contractId])


    const handleDelete = async () => {
        const res = await bootbox.confirm(t('validations:messages.confirmDelete', { context: 'female', entity: t('glossary:medical-prescription')?.toLowerCase(), name: `"${medicalPrescription.name}"` }));
        if (!res) return;
        
        mutateDeleteMedicalPrescription({
            medicalPrescriptionId: medicalPrescription._id
        })
    }

    const handleDeleteAttachment = (attachment) => () => {
        onDeleteAttachment(medicalPrescription, attachment)
    }


    const handleAddMedicine = () => {
        append({
            _id: getMongoObjectId(),
            name: 'Medicamento',
            quantity: 1
        })
    }

    return (
        <ListGroupItem>
            <Media>
                <Media body>
                        <div className="d-flex align-items-center float-right">
                            <Link to={`/contracts/${medicalPrescription.contract?._id}/medical-prescriptions/${medicalPrescription._id}`}  target="_blank" >
                                <i className="fas fa-external-link-alt" />
                            </Link>
                            <i
                                className="fas fa-times text-danger pointer ml-2"
                                title={t('contracts.milestones.delete')}
                                data-action="delete-milestone"
                                onClick={handleDelete}
                            />
                        </div>
                        <FormGroup className="m-0" inline>
                            <Label><b>{t('milestones.name')}</b></Label>
                            <Input 
                                bsSize="sm" 
                                type="text" 
                                id="name" 
                                name="name" 
                                innerRef={register} 
                                defaultValue={name} 
                                placeholder={t('milestones.name')} 
                            />
                        </FormGroup>
                        <FormGroup className="mt-1">
                            <Label><b>{t('Medicamentos')}</b> </Label>
                            <Badge className="ml-2 text-white pointer" pill color="primary" onClick={handleAddMedicine} ><i className="fas fa-plus" /> Agregar</Badge>
                            
                            {
                                fields.map((field, i) => (
                                    <MedicineFormItem 
                                        medicine={field}
                                        name={`medicines[${i}]`}
                                        register={register}
                                        control={control}
                                        setValue={setValue}
                                    />
                                ))
                            }
                            <FormFeedback>{t('participants.emailNotValid')}</FormFeedback>
                        </FormGroup>

                        {attachments &&
                            <Fragment>
                                <label className="milestone-attachment-title">
                                    {t('contracts.milestones.attachments')}
                                </label>
                               <ul className="milestone-attachment-detail">
                                    {attachments.map((attachment, i) => {
                                        return (
                                            <li key={`${attachment._id}-${i}`}>
                                                <a className="text-break" href={attachment.url} target="_blank" rel="noopener noreferrer">{attachment.name}</a>
                                                {!completed && !attachment.isUploading &&
                                                    <span
                                                        className="ml-2"
                                                        href="#"
                                                        data-action="delete-attachment"
                                                        title={t('contracts.milestones.delete_attachment')}
                                                        data-id={attachment._id}
                                                        data-name={attachment.name}
                                                        onClick={handleDeleteAttachment(attachment)}
                                                    >
                                                        <i className="fas fa-times text-danger" data-name="{{name}}" data-id="{{_id}}" />
                                                    </span>
                                                }
                                                {
                                                    attachment.isUploading &&
                                                    <span className="d-flex align-items-center">
                                                        {/* <Progress animated color="success" value={progress} /> */}
                                                        <Spinner className="align-self-center" style={{ width: '1rem', height: '1rem' }} /> <span className="ml-1"> Subiendo... </span>
                                                    </span>
                                                }

                                            </li>
                                        )
                                    })}
                                </ul>
                            </Fragment>
                        }
                        <div className="d-flex justify-content-end">
                            <Button color="primary" onClick={handleSubmit(handleSave)}>Guardar</Button>
                            <Button className="ml-1" color="light" onClick={() => onEditing(false)}>Cancelar</Button>
                        </div>
                        
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function MedicineFormItem({ name, medicine, register, control, setValue }) {
    const { t } = useTranslation();
    const { fields, append, remove } = useFieldArray({
        control, 
        name: `${name}.attachments`,
    })
    const isUnlimited = useWatch({
        control,
        name: `${name}.isUnlimited`
    })

    const { chooseFileAndUploadFile, }  = useFileUpload({
        onSuccess: async (file, variables) => {
            append({
                name: file.name,
                url: file.url,
                file
            })
        },
    })

    const handleAddEvidence = async () => {
        const name = await bootbox.prompt(t('contracts.documents.attachment_name'));

        if (!name || name === '') return toast.error(t('contracts.documents.attachment_name_missing'));

        chooseFileAndUploadFile({ name }, {
            accept: ".pdf,.docx,.doc,image/*",
        })
    }

    const handleRemoveEvidence = async (attachment, attachmentIndex) => {
        const res = await bootbox.confirm(t('contracts.milestones.confirm_delete_attachment', { name: attachment.name }));
        if (!res) return;

        remove(attachmentIndex)
    }

    const handleQuantityChange = (evt) => {
        console.log({c: evt.target.value})
        if (Number(evt.target.value) < 1) setValue(`${name}.isUnlimited`,  1)
    }

    return (
        <div className="pl-3 ">
                <FormGroup className="m-0" inline>
                    <Label><b>{t('milestones.name')}</b></Label>
                    <Input 
                        bsSize="sm" 
                        type="text" 
                        name={`${name}.name`}
                        id={`${name}.name`}
                        innerRef={register()} 
                        defaultValue={medicine.name} 
                        placeholder={t('milestones.name')} 
                    />
                </FormGroup>
                <FormGroup className="m-1" inline>
                    <Label className="d-flex">
                        <b>{t('Cantidad')}</b>
                        <CustomInput 
                            bsSize="sm" 
                            type="switch" 
                            className="ml-2"
                            name={`${name}.isUnlimited`}
                            id={`${name}.isUnlimited`}
                            innerRef={register()} 
                            label={t('Ilimitada')}
                            defaultChecked={medicine.isUnlimited} 
                        />
                    </Label>
                    <Input 
                        bsSize="sm" 
                        type="number" 
                        name={`${name}.quantity`}
                        id={`${name}.quantity`}
                        innerRef={register()} 
                        onBlur={handleQuantityChange}
                        min={1}
                        disabled={isUnlimited}
                        defaultValue={medicine.quantity} 
                        placeholder={t('Cantidad')} 
                    />
                </FormGroup>
                {/* <input 
                    type="hidden"  
                    id={`${name}.quantitySupplied`}
                    name={`${name}.quantitySupplied`}
                    ref={register()} 
                    defaultValue={medicine.quantitySupplied} 
                /> */}
                <input 
                    type="hidden"  
                    id={`${name}._id`}
                    name={`${name}._id`}
                    ref={register()} 
                    defaultValue={medicine._id} 
                />
                {/* <input 
                    type="hidden"  
                    id={`${name}.supplyHistory`}
                    name={`${name}.supplyHistory`}
                    innerRef={register()} 
                    defaultValue={JSON.stringify(medicine.supplyHistory)} 
                /> */}
                <FormGroup className="mt-1" hidden>
                    <Label><b>{t('Evidencia')}</b> </Label>
                    <Badge className="ml-2 text-white pointer" pill color="primary" onClick={handleAddEvidence}>
                        <i className="fas fa-plus"  /> Agregar
                    </Badge>
                    
                    {
                        fields.map((field, i) => (
                            <AttachmentFormItem 
                                name={`${name}.attachments[${i}]`}
                                attachment={field}
                                register={register}
                                onRemove={handleRemoveEvidence}
                            />
                        ))
                    }
                    <FormFeedback>{t('participants.emailNotValid')}</FormFeedback>
                </FormGroup>          
        </div>
    )
}

function AttachmentFormItem({name, attachment,index,  register, onRemove}) {
    const { t } = useTranslation();

    const handleRemove = () => {
        onRemove(attachment, index)
    }

    return (
        <div>
            <FormGroup className="m-0" inline>
                    <Label>
                        <b>{t('milestones.name')}</b>
                        <a
                            className="ml-2"
                            href={attachment.url}
                            hidden={!attachment.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="fas fa-eye" />
                        </a>
                        {!attachment.isUploading &&
                            <span
                                className="ml-2 pointer"
                                href="#"
                                data-action="delete-attachment"
                                title={t('contracts.milestones.delete_attachment')}
                                data-id={attachment._id}
                                data-name={attachment.name}
                                onClick={handleRemove}
                            >
                                <i className="fas fa-times text-danger" />
                            </span>
                        }
                    </Label>
                    <Input 
                        bsSize="sm" 
                        type="text" 
                        name={`${name}.name`}
                        id={`${name}.name`}
                        innerRef={register()} 
                        defaultValue={attachment.name} 
                        placeholder={t('milestones.name')} 
                    />
                    <input type="hidden" name={`${name}.url`} defaultValue={attachment.url}  ref={register()} />
                </FormGroup>
        </div>
    )
}